import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import type { Tarea, Factura, Cliente, ConfigTarifaCliente, ConfigDetraccion } from '@/lib/types'
import { DEFAULT_DETRACCION, contactoFacturacionDe } from '@/lib/types'
import { r2, formatCurrency, calcularDetraccion } from '@/lib/utils'

interface Props {
  proyectoId: number
  proyectoNombre: string
  cliente: Cliente | null
  tareas: Tarea[]
  facturas: Factura[]
  tarifaCliente: ConfigTarifaCliente | null
  moneda: string
  preSelectedIds?: number[]
  // Cotizaciones vinculadas al proyecto (M:N). Si hay exactamente una, la factura
  // hereda su cotizacion_id (trazabilidad cotización→factura, DAT-04).
  proyectoCotizaciones?: { cotizacion_id: number }[]
  onClose: () => void
  onSaved: () => void
}

export default function FacturarTareasModal({
  proyectoId,
  proyectoNombre,
  cliente,
  tareas,
  tarifaCliente,
  moneda,
  preSelectedIds,
  proyectoCotizaciones,
  onClose,
  onSaved,
}: Props) {
  const db = useSupabase()
  const api = useApi()
  const nav = useNavigate()

  // Filter: only approved tasks without an existing invoice
  const facturables = useMemo(() =>
    tareas.filter(t =>
      t.estado_aprobacion === 'aprobada' && !t.factura_id
    ), [tareas])

  const [selected, setSelected] = useState<Set<number>>(() => {
    if (preSelectedIds && preSelectedIds.length > 0) {
      // Only pre-select IDs that are actually in the facturables list
      const facturableIds = new Set(tareas.filter(t => t.estado_aprobacion === 'aprobada' && !t.factura_id).map(t => t.id))
      return new Set(preSelectedIds.filter(id => facturableIds.has(id)))
    }
    return new Set()
  })
  const [igvPct, setIgvPct] = useState(18)
  const [igvMode, setIgvMode] = useState<'agregar' | 'incluido'>('agregar')
  const [notas, setNotas] = useState('')
  const [saving, setSaving] = useState(false)

  // Config SPOT
  const [configSpot] = useState<Omit<ConfigDetraccion, 'id' | 'created_at'>>(DEFAULT_DETRACCION)

  const tarifa = tarifaCliente?.tarifa_hora ?? 0

  function toggleAll() {
    if (selected.size === facturables.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(facturables.map(t => t.id)))
    }
  }

  function toggle(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Calculate amounts for each selected task
  const selectedTareas = useMemo(() =>
    facturables.filter(t => selected.has(t.id)), [facturables, selected])

  const lineItems = useMemo(() =>
    selectedTareas.map(t => {
      let monto = t.monto || 0
      let desc = t.titulo

      if (t.tipo_cobro === 'por_horas' && t.horas_estimadas > 0) {
        const rate = tarifa > 0 ? tarifa : 0
        monto = r2(t.horas_estimadas * rate)
        desc = `${t.titulo} (${t.horas_estimadas}h × ${formatCurrency(rate, moneda)})`
      } else if (t.tipo_cobro === 'monto_fijo') {
        desc = `${t.titulo}`
      }

      return {
        tareaId: t.id,
        tipo: 'fijo' as const,
        descripcion: desc,
        precio_unitario: monto,
        cantidad: 1,
        total: monto,
      }
    }), [selectedTareas, tarifa, moneda])

  const rawTotal = r2(lineItems.reduce((s, i) => s + i.total, 0))

  // IGV mode: 'agregar' = add on top, 'incluido' = extract from total
  const subtotal = igvMode === 'incluido'
    ? r2(rawTotal / (1 + igvPct / 100))
    : rawTotal
  const igvMonto = igvMode === 'incluido'
    ? r2(rawTotal - subtotal)
    : r2(rawTotal * igvPct / 100)
  const total = igvMode === 'incluido'
    ? rawTotal
    : r2(rawTotal + igvMonto)
  const totalHoras = r2(selectedTareas.reduce((s, t) => s + (t.horas_estimadas || 0), 0))

  async function handleGenerar() {
    if (selected.size === 0) {
      showToast('Selecciona al menos una tarea', 'error')
      return
    }
    if (saving) return
    setSaving(true)

    try {
      // Load config SPOT from DB
      let spotConfig = configSpot
      try {
        const configs = await db.getAll<ConfigDetraccion>('config_detraccion')
        const active = configs.find(c => c.activo)
        if (active) spotConfig = active
      } catch { /* use default */ }

      // FIS-03: para monedas != PEN hay que convertir a soles para evaluar la
      // detracción (umbral y monto van en PEN). Antes se pasaba null → total_pen 0 →
      // la detracción NUNCA aplicaba en facturas USD/EUR. Cargamos el TC (regla SUNAT:
      // exacto por fecha, o el último disponible anterior).
      let tc: number | null = null
      if (moneda !== 'PEN') {
        try {
          const hoy = new Date().toISOString().slice(0, 10)
          const tcs = await db.getAll<{ fecha: string; venta: number }>('tipo_cambio')
          const exacto = tcs.find(t => t.fecha === hoy)
          const anterior = tcs
            .filter(t => t.fecha <= hoy)
            .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))[0]
          const elegido = exacto ?? anterior
          if (elegido) tc = Number(elegido.venta)
        } catch { /* sin TC: la detracción no aplicará; se puede ajustar al editar */ }
      }

      // Detraccion (en PEN)
      const detraccion = calcularDetraccion(total, moneda, tc, spotConfig.umbral_pen, spotConfig.porcentaje)

      // Build factura items (strip tareaId)
      const items = lineItems.map(({ tipo, descripcion, precio_unitario, cantidad, total: t }) => ({
        tipo, descripcion, precio_unitario, cantidad, total: t,
      }))

      // El número lo asigna el servidor (atómico); no se envía numero.
      // Contacto congelado en la factura: el de FACTURACIÓN desde la relación.
      const factCt = contactoFacturacionDe(cliente)
      const record: Record<string, unknown> = {
        cliente_id: cliente?.id ?? null,
        cliente_nombre: cliente?.empresa ?? '',
        cliente_contacto: factCt ? [factCt.nombre, factCt.apellido].filter(Boolean).join(' ') : '',
        cliente_ruc: cliente?.ruc_dni ?? '',
        cliente_email: factCt?.email ?? '',
        proyecto_nombre: proyectoNombre,
        proyecto_id: proyectoId,  // FK real al proyecto (DAT-03)
        // Trazabilidad: solo si el proyecto tiene UNA cotización vinculada (DAT-04).
        cotizacion_id: proyectoCotizaciones?.length === 1 ? proyectoCotizaciones[0].cotizacion_id : null,
        fecha_emision: new Date().toISOString().slice(0, 10),
        fecha_vencimiento: null,
        items,
        adjuntos: [],
        subtotal,
        igv_porcentaje: igvPct,
        igv_monto: igvMonto,
        total,
        moneda,
        estado: 'borrador',
        notas: notas || null,
        tipo_cambio: tc,
        detraccion_aplica: detraccion.aplica,
        detraccion_porcentaje: spotConfig.porcentaje,
        detraccion_monto_pen: detraccion.montoPen,
        total_pen: detraccion.totalPen,
      }

      // Numeración atómica + creación + enlace de tareas en una transacción (LOG-01/02).
      const created = await api.post<Factura>('/api/facturas/from-tareas', {
        factura: record,
        tareaIds: selectedTareas.map(t => t.id),
      })

      showToast(`Factura ${created.numero} generada con ${selected.size} tarea${selected.size > 1 ? 's' : ''}`, 'success')
      onSaved()
      nav(`/facturas/${created.id}`)
    } catch (err) {
      console.error('Error generating invoice:', err)
      showToast(err instanceof Error ? err.message : 'Error al generar factura', 'error')
    } finally {
      setSaving(false)
    }
  }

  const prioBadge: Record<string, { color: string; bg: string }> = {
    urgente: { color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
    alta: { color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 12%, transparent)' },
    media: { color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
    baja: { color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
  }

  return (
    <Modal isOpen onClose={onClose} title="Generar Factura desde Tareas" closeOnOverlay={false}>
      <div style={{ maxHeight: '60vh', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {facturables.length === 0 ? (
          <div className="empty-state" style={{ padding: '2rem' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/><path d="M8 12h8"/></svg>
            <h3>Sin tareas facturables</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              No hay tareas con aprobación confirmada sin facturar.
            </p>
          </div>
        ) : (
          <>
            {/* Select all */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={selected.size === facturables.length}
                  onChange={toggleAll}
                />
                Seleccionar todas ({facturables.length})
              </label>
              {tarifa > 0 && (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Tarifa: {formatCurrency(tarifa, moneda)}/h
                </span>
              )}
            </div>

            {/* Task list */}
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              {facturables.map(t => {
                const isSelected = selected.has(t.id)
                const prio = prioBadge[t.prioridad] || prioBadge.media
                let monto = t.monto || 0
                if (t.tipo_cobro === 'por_horas' && t.horas_estimadas > 0 && tarifa > 0) {
                  monto = r2(t.horas_estimadas * tarifa)
                }
                return (
                  <div
                    key={t.id}
                    onClick={() => toggle(t.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border)',
                      background: isSelected ? 'color-mix(in srgb, var(--primary-light) 8%, transparent)' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                  >
                    <input type="checkbox" checked={isSelected} readOnly style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.titulo}
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                        <span style={{
                          fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4,
                          background: prio.bg, color: prio.color, fontWeight: 600,
                        }}>
                          {t.prioridad}
                        </span>
                        {t.tipo_cobro === 'por_horas' && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {t.horas_estimadas}h
                          </span>
                        )}
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {t.tipo_cobro === 'sin_costo' ? 'Sin costo' : t.tipo_cobro === 'por_horas' ? 'Por horas' : 'Monto fijo'}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: monto > 0 ? 'var(--accent-primary)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {monto > 0 ? formatCurrency(monto, moneda) : '—'}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Summary */}
            {selected.size > 0 && (
              <div style={{
                background: 'var(--bg-secondary)', borderRadius: 8, padding: 14,
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '0.85rem',
              }}>
                <div>Tareas seleccionadas: <strong>{selected.size}</strong></div>
                <div>Horas totales: <strong>{totalHoras}h</strong></div>
                <div>Subtotal: <strong>{formatCurrency(subtotal, moneda)}</strong></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <select
                    className="form-input"
                    value={igvMode}
                    onChange={e => setIgvMode(e.target.value as 'agregar' | 'incluido')}
                    style={{ padding: '2px 6px', fontSize: '0.82rem', width: 'auto', minWidth: 100 }}
                  >
                    <option value="agregar">+ Agregar IGV</option>
                    <option value="incluido">IGV incluido</option>
                  </select>
                  <input
                    type="number" value={igvPct} min={0} max={100}
                    onChange={e => setIgvPct(Number(e.target.value))}
                    className="form-input"
                    style={{ width: 50, padding: '2px 6px', fontSize: '0.85rem' }}
                  />
                  % = <strong>{formatCurrency(igvMonto, moneda)}</strong>
                </div>
                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border)', paddingTop: 8, fontSize: '1rem' }}>
                  Total: <strong style={{ color: 'var(--accent-primary)', fontSize: '1.1rem' }}>{formatCurrency(total, moneda)}</strong>
                </div>
              </div>
            )}

            {/* Notes */}
            <textarea
              className="form-input"
              placeholder="Notas de la factura (opcional)"
              value={notas}
              onChange={e => setNotas(e.target.value)}
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </>
        )}
      </div>

      {/* Footer */}
      <div className="modal-actions" style={{ marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
        {facturables.length > 0 && (
          <button
            className="btn btn-primary"
            onClick={handleGenerar}
            disabled={saving || selected.size === 0}
          >
            {saving ? (
              <><div className="auth-spinner" style={{ width: 16, height: 16 }} /> Generando...</>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                Generar Factura ({selected.size})
              </>
            )}
          </button>
        )}
      </div>
    </Modal>
  )
}
